import { Minus, Package, Pencil, Plus, X } from "lucide-react";
import {
  DrawerClose,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import productPlaceholder from "../../assets/product-placeholder.svg";
import { formatCurrency, resolveProductUnitLabel } from "../../lib/productUtils";

const productImageFallback = productPlaceholder;

/**
 * DrawerCart — full cart sheet.
 *
 * Opened by the header cart icon or the sticky footer cart button.
 * Configurable lines show a "Modifica" link that re-opens DrawerProductDetail.
 */
export function DrawerCart({
  cartLines,
  cartSummary,
  primaryColor,
  onChangeLineQty, // (line: CartLine, delta: number) => void
  onEditLine,      // (line: CartLine) => void — only for configurable lines
  onCheckout,
}) {
  const empty = cartSummary.items <= 0;

  return (
    <div className="max-h-[86vh] overflow-auto pb-4">
      <DrawerHeader className="relative pb-2 text-left">
        <DrawerTitle className="text-xl font-semibold text-[#0f172a]">Il tuo carrello</DrawerTitle>
        <DrawerDescription className="text-sm text-[#64748b]">
          {cartSummary.items} prodott{cartSummary.items === 1 ? "o" : "i"}{" "}
          selezionat{cartSummary.items === 1 ? "o" : "i"}.
        </DrawerDescription>
        <DrawerClose asChild>
          <button
            type="button"
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b]"
            aria-label="Chiudi carrello"
          >
            <X className="h-4 w-4" />
          </button>
        </DrawerClose>
      </DrawerHeader>

      <div className="px-4">
        {empty ? (
          <p className="py-6 text-center text-sm text-[#64748b]">Il carrello è vuoto.</p>
        ) : (
          <div className="rounded-2xl border border-[#e2e8f0] bg-white">
            {cartLines.map((line) => {
              const lineSummary =
                [
                  line.variantLabel,
                  ...(Array.isArray(line.optionSummaries)
                    ? line.optionSummaries.map((e) => `${e.label}: ${e.value}`)
                    : []),
                ]
                  .filter(Boolean)
                  .join(" • ") || resolveProductUnitLabel(line.productSnapshot);

              return (
                <div key={line.id} className="border-b border-[#eef2f7] px-3 py-2.5 last:border-b-0">
                  <div className="flex items-center gap-2.5">
                    {line.imageUrl ? (
                       <img
                        src={line.imageUrl || productImageFallback}
                        alt={line.productName}
                        className="h-12 w-12 shrink-0 rounded-lg object-cover"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = productImageFallback; }}
                      />
                    ) : (
                       <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-200 text-slate-500">
                          <Package className="h-4 w-4" />
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                      {line.isConfigurable ? (
                        <button
                          type="button"
                          onClick={() => onEditLine(line)}
                          className="truncate text-left text-sm font-semibold text-[#0f172a]"
                        >
                          {line.productName}
                        </button>
                      ) : (
                        <p className="truncate text-sm font-semibold text-[#0f172a]">{line.productName}</p>
                      )}
                      <p className="truncate text-xs text-[#64748b]">{lineSummary}</p>
                      {line.notes ? (
                        <p className="mt-0.5 line-clamp-2 text-xs italic text-[#64748b]">Nota: {line.notes}</p>
                      ) : null}
                      {Number(line.unitPrice) > 0 ? (
                        <p className="mt-0.5 text-xs font-medium text-[#475569]">{formatCurrency(line.unitPrice)}</p>
                      ) : null}
                    </div>

                    <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
                      <div className="flex items-center gap-1.5">
                        {line.isConfigurable && (
                          <button
                            type="button"
                            onClick={() => onEditLine(line)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#64748b]"
                            aria-label="Modifica"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <div className="inline-flex h-8 items-center rounded-lg border border-[#dbe5ef]">
                          <button
                            type="button"
                            onClick={() => onChangeLineQty(line, -1)}
                            className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-[24px] text-center text-sm font-semibold">
                            {line.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => onChangeLineQty(line, 1)}
                            className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      {((Number(line.unitPrice) || 0) * (Number(line.quantity) || 0)) > 0 ? (
                        <p className="text-sm font-semibold text-[#020617]">
                          {formatCurrency((Number(line.unitPrice) || 0) * (Number(line.quantity) || 0))}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!empty && (
          <>
            <div className="mt-3 flex items-center justify-between rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2">
              <div>
                <p className="text-xs font-medium text-[#64748b]">Totale</p>
                <p className="text-sm font-semibold text-[#0f172a]">
                  {cartSummary.items} prodott{cartSummary.items === 1 ? "o" : "i"}
                </p>
              </div>
              <p className="text-xl font-bold text-[#020617]">{formatCurrency(cartSummary.total)}</p>
            </div>
            <button
              type="button"
              onClick={onCheckout}
              className="mt-3 flex h-11 w-full items-center justify-center rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Vai al checkout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
